<template>
    <div>
        <div class="row mx-1">
            <div class="col-lg-3 col-md-4 col-12 pe-0">
                <img :src="transactionLogo" class="transacation-logo" alt="logo" />
                <div>
                    <label class="mb-2">{{ transactionLabel }}</label>
                    <Datepicker v-model="filter.dateRange" :partial-range="false" :enable-time-picker="false" :clearable="false"
                                :disable="loading" multi-calendars range />
                </div>
            </div>
            <div class="col-lg-5 col-md-4 col-6 d-flex align-items-end">
                <button class="btn bg-gradient-dark mb-0" :disable="loading" @click="getTransactions">
                    <i class="fas fa-search" v-if="!loading"></i>

                    <div class="spinner-border spinner-border-sm" role="status" v-if="loading">
                        <span class="sr-only">Loading...</span>
                    </div>

                    Search
                </button>
            </div>
            <div class="col-lg-4 col-md-4 col-6 d-flex align-items-end">
                <button class="btn bg-gradient-dark mb-0 ms-auto" :disabled="exportLoading" @click="onExportClicked">
                    <i class="fas fa-download" v-if="!exportLoading"></i>

                    <div class="spinner-border spinner-border-sm" role="status" v-if="exportLoading">
                        <span class="sr-only">Loading...</span>
                    </div>
                    Export
                </button>
            </div>
        </div>
        <div class="table-responsive p-0">
            <table class="table main-table">
                <thead>
                    <tr v-if="selectedType.toLowerCase() === 'smart'">
                        <th>Date </th>
                        <th>Category</th>
                        <th>Dealer </th>
                        <th> Retailer </th>
                        <th>Retailer User</th>
                        <th>Details</th>
                        <th>Amount</th>
                        <th>Reference Number</th>
                    </tr>
                    <tr v-else-if="selectedType.toLowerCase() === 'bayad'">
                        <th>Date </th>
                        <th>Biller Category</th>
                        <th>Biller Name</th>
                        <th>Dealer </th>
                        <th> Retailer </th>
                        <th>Retailer User</th>
                        <th>Amount</th>
                        <th>Reference Number</th>
                    </tr>
                    <tr v-else-if="selectedType.toLowerCase() === 'both'">
                        <th>Date </th>
                        <th>Type</th>
                        <th>Category/Biller</th>
                        <th>Dealer </th>
                        <th> Retailer </th>
                        <th>Retailer User</th>
                        <th>Details</th>
                        <th>Amount</th>
                        <th>Reference Number</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="loading">
                        <td colspan="9">
                            <div class="text-center">
                                <Vue3Lottie :animationData="searchingJSON" class="height-150 py-2" />
                            </div>
                        </td>
                    </tr>
                    <tr v-if="!loading && pageItems.length == 0">
                        <td colspan="9">
                            <div class="text-center">
                                <Vue3Lottie :animationData="noDataJSON" class="height-150 py-2" />
                                <p class="text-xs font-weight-bold">No Data Found</p>
                            </div>
                        </td>
                    </tr>
                    <!-- Smart Transaction Rows -->
                    <tr v-if="selectedType.toLowerCase() === 'smart'" v-for="(transaction, index) in pageItems" :key="'smart-' + index">
                        <td>
                            {{ $format.FormatFullDateTime(transaction.date) }}
                        </td>
                        <td>
                            <div class="text-uppercase d-flex justify-content-start align-items-center">
                                <span>{{ transaction.categoryWithItems }}</span>
                            </div>
                        </td>
                        <td>
                            <div class="d-flex flex-column justify-content-center">
                                <p class="main-text"> {{ transaction.retailer.name }}</p>
                                <p class="sub-text"> {{ transaction.retailer.corporateID }}</p>
                            </div>
                        </td>
                        <td>
                            <div class="d-flex flex-column justify-content-center">
                                <p class="main-text"> {{ transaction.subRetailer.name }}</p>
                                <p class="sub-text">{{ transaction.subRetailer.branch }}</p>
                            </div>
                        </td>
                        <td>
                            <div class="d-flex flex-column justify-content-center">
                                <p class="main-text"> {{ transaction.subRetailerUser.name }}</p>
                                <p class="sub-text"> {{ transaction.subRetailerUser.username }}</p>
                            </div>
                        </td>
                        <td> {{ transaction.details }}</td>
                        <td>
                            <span class="badge badge-sm bg-gradient-info" :class="isBundle(transaction) ? 'bg-gradient-bundle' : 'bg-gradient-info'">
                                Php {{ (transaction.totalAmount || 0).toFixed(2) }}
                            </span>
                        </td>
                        <td> {{ transaction.referenceNumber }} </td>
                    </tr>
                    <!-- Bayad Transaction Rows -->
                    <tr v-else-if="selectedType.toLowerCase() === 'bayad'" v-for="(transaction, index) in pageItems" :key="'bayad-' + index">
                        <td>
                            {{ $format.FormatFullDateTime(transaction.date) }}
                        </td>
                        <td>
                            <div class="text-uppercase d-flex justify-content-start align-items-center">
                                <span>{{ transaction.categoryOfBillers }}</span>
                            </div>
                        </td>
                        <td>
                            {{ transaction.nameOfBiller }}
                        </td>
                        <td>
                            <div class="d-flex flex-column justify-content-center">
                                <p class="main-text"> {{ transaction.dealer?.name || 'N/A' }}</p>
                                <p class="sub-text"> {{ transaction.dealer?.corporateID || '' }}</p>
                            </div>
                        </td>
                        <td>
                            <div class="d-flex flex-column justify-content-center">
                                <p class="main-text"> {{ transaction.retailer?.name || 'N/A' }}</p>
                                <p class="sub-text">{{ transaction.retailer?.branch || '' }}</p>
                            </div>
                        </td>
                        <td>
                            <div class="d-flex flex-column justify-content-center">
                                <p class="main-text"> {{ transaction.retailerUser?.name || 'N/A' }}</p>
                                <p class="sub-text"> {{ transaction.retailerUser?.username || '' }}</p>
                            </div>
                        </td>
                        <td>
                            <span class="badge badge-sm bg-gradient-success">
                                Php {{ (transaction.totalAmountDue || 0).toFixed(2) }}
                            </span>
                        </td>
                        <td> {{ transaction.referenceNumber }} </td>
                    </tr>
                    <!-- Both Transaction Rows -->
                    <tr v-else-if="selectedType.toLowerCase() === 'both'" v-for="(transaction, index) in pageItems" :key="'both-' + index">
                        <td>
                            {{ $format.FormatFullDateTime(transaction.date) }}
                        </td>
                        <td>
                            <span class="badge badge-sm" :class="transaction.type === 'Smart' ? 'bg-gradient-info' : 'bg-gradient-success'">
                                {{ transaction.type }}
                            </span>
                        </td>
                        <td>
                            <div class="text-uppercase d-flex justify-content-start align-items-center">
                                <span v-if="transaction.type === 'Smart'">{{ transaction.categoryWithItems }}</span>
                                <span v-else>{{ transaction.categoryOfBillers }} - {{ transaction.nameOfBiller }}</span>
                            </div>
                        </td>
                        <td>
                            <div class="d-flex flex-column justify-content-center">
                                <p class="main-text"> {{ transaction.retailer?.name || transaction.dealer?.name || 'N/A' }}</p>
                                <p class="sub-text"> {{ transaction.retailer?.corporateID || transaction.dealer?.corporateID || '' }}</p>
                            </div>
                        </td>
                        <td>
                            <div class="d-flex flex-column justify-content-center">
                                <p class="main-text"> {{ transaction.subRetailer?.name || transaction.retailer?.name || 'N/A' }}</p>
                                <p class="sub-text">{{ transaction.subRetailer?.branch || transaction.retailer?.branch || '' }}</p>
                            </div>
                        </td>
                        <td>
                            <div class="d-flex flex-column justify-content-center">
                                <p class="main-text"> {{ transaction.subRetailerUser?.name || transaction.retailerUser?.name || 'N/A' }}</p>
                                <p class="sub-text"> {{ transaction.subRetailerUser?.username || transaction.retailerUser?.username || '' }}</p>
                            </div>
                        </td>
                        <td>
                            {{ transaction.details || 'N/A' }}
                        </td>
                        <td>
                            <span class="badge badge-sm" :class="transaction.type === 'Smart' ? 'bg-gradient-info' : 'bg-gradient-success'">
                                Php {{ (transaction.totalAmount || transaction.totalAmountDue || 0).toFixed(2) }}
                            </span>
                        </td>
                        <td> {{ transaction.referenceNumber }} </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <div class="paginate-div d-flex align-items-end mt-3 me-3 custom-pagination">
        <vue-awesome-paginate class="ms-auto" :max-pages-shown="5" :total-items="transactions.length"
                              :items-per-page="itemPerPage" :on-click="onPageChanged" v-model="currentPage" />
    </div>
</template>

<script>
    import { mapActions, mapMutations, mapState } from "vuex";
    import noDataJSON from "@/assets/no-data.json";
    import searchingJSON from "@/assets/searching.json";

    export default {
        data() {
            return {
                noDataJSON,
                searchingJSON,
                itemPerPage: 10,
                pageItems: [],
            };
        },
        props: {
            selectedType: {
                type: String,
                default: null,
            },
        },
        computed: {
            ...mapState({
                currentPage: (state) => state.retailerController.transactionsCurrentPage,
                filter: (state) => state.retailerController.transactionsFilter,
                loading: (state) => state.retailerController.transactionsLoading,
                transactions: (state) => state.retailerController.transactions,
                exportLoading: (state) =>
                    state.retailerController.transactionsExportLoading,
            }),

            transactionLogo() {
                switch (this.selectedType.toLowerCase()) {
                    case "bayad":
                        return "/img/bayad_logo.png";
                    case "smart":
                        return "/img/masterdealer.png";
                    default:
                        return "/img/default.png";
                }
            },
            transactionLabel() {
                return `${this.selectedType} Transaction Date`;
            },
        },
        watch: {
            selectedType: {
                handler(newType) {
                    if (newType) {
                        this.getTransactions();
                    }
                },
                immediate: true,
            },
            transactions: {
                handler() {
                    this.onPageChanged(1);
                },
                deep: true,
            },
        },
        methods: {
            ...mapActions({
                getMasterDealerTransactions: "retailerController/GetMasterDealerTransactions",
                exportTransactions: "retailerController/ExportTypeTransactions",
            }),
            ...mapMutations({
                setCurrentPage: "retailerController/SET_TransactionsCurrentPage",
                setTransactionsFilter: "retailerController/SET_TransactionsFilter",
            }),
            onPageChanged(page) {
                this.setCurrentPage(page);

                // No need to filter here since the backend already filters by type
                this.pageItems = this.transactions.slice(
                    (page - 1) * this.itemPerPage,
                    page * this.itemPerPage
                );
            },
            async getTransactions() {
                const dateRange = this.filter.dateRange;
                this.setTransactionsFilter({
                    ...this.filter,
                    dateFrom: dateRange[0],
                    dateTo: dateRange[1],
                    type: this.selectedType,
                });

                await this.getMasterDealerTransactions();
            },
            onExportClicked() {
                const dateRange = this.filter.dateRange;
                this.setTransactionsFilter({
                    ...this.filter,
                    dateFrom: dateRange[0],
                    dateTo: dateRange[1],
                    type: this.selectedType,
                });

                this.exportTransactions()
                    .then(() => {
                        this.$toast.success("Export completed.");
                    })
                    .catch(() => {
                        this.$toast.error("Export failed.");
                    });
            },
            isBundle(transaction) {
                return transaction.categoryWithItems?.toLowerCase().includes('bundle');
            },
        },
        async mounted() {
            // Set initial date range if not already set
            if (!this.filter.dateRange || this.filter.dateRange.length === 0) {
                const today = new Date();
                const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

                this.setTransactionsFilter({
                    ...this.filter,
                    dateRange: [firstDayOfMonth, today],
                });
            }

            // Initial load will be triggered by the watch handler
        },
    };
</script>


<style>
    .custom-pagination .paginate-buttons {
        min-width: 35px;
        width: auto !important; /*override the default width to better show pagination at 3 digits*/
    }
</style>